import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useSites } from '@/hooks/useSites';
import { useClients } from '@/hooks/useClients';
import { useFacilityManagement } from '@/hooks/useFacilityManagement';
import { Building2, Plus, Edit2, MapPin, Phone, Users, Home, ChevronRight } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { mockSites, mockSiteClients } from '@/data/mockData';
import { RoomConfigSection, type RoomConfig } from './RoomConfigSection';
type FacilityType = 'men' | 'women' | 'family';
interface UnifiedFacilityManagementProps {
  isDemo?: boolean;
  onSelectFacility?: (facility: any) => void;
  canAddFacility?: boolean;
}
export function UnifiedFacilityManagement({
  isDemo = false,
  onSelectFacility,
  canAddFacility = true
}: UnifiedFacilityManagementProps) {
  const {
    sites: dbSites
  } = useSites();
  const {
    clients: dbClients
  } = useClients();
  const {
    createFacility,
    updateFacility
  } = useFacilityManagement();

  // Local state for demo mode edits
  const [demoSites, setDemoSites] = useState(mockSites);

  // Use mock data in demo mode
  const sites = isDemo ? demoSites : dbSites;
  const allClients = isDemo ? Object.values(mockSiteClients).flat() : dbClients;

  // Calculate client counts per facility
  const getClientCount = (siteId: string) => {
    if (isDemo) {
      return (mockSiteClients[siteId as keyof typeof mockSiteClients] || []).length;
    }
    return allClients.filter((c: any) => c.site_id === siteId || c.siteId === siteId).length;
  };
  const [selectedSite, setSelectedSite] = useState<string>('all');
  const [isAddFacilityOpen, setIsAddFacilityOpen] = useState(false);
  const [editingFacility, setEditingFacility] = useState<any>(null);
  const [facilityName, setFacilityName] = useState('');
  const [facilityAddress, setFacilityAddress] = useState('');
  const [facilityPhone, setFacilityPhone] = useState('');
  const [facilityCapacity, setFacilityCapacity] = useState('');
  const [facilityType, setFacilityType] = useState<FacilityType>('men');
  const [roomConfig, setRoomConfig] = useState<RoomConfig>({
    singleRooms: '0',
    sharedRooms: '15',
    communalRooms: '0',
    communalBeds: '4',
    startingNumber: '101',
  });
  const getFacilityTypeLabel = (type: FacilityType) => {
    switch (type) {
      case 'family':
        return 'Family';
      case 'women':
        return 'Women';
      case 'men':
        return 'Men';
      default:
        return 'Men';
    }
  };
  const getFacilityTypeFromSite = (site: any): FacilityType => {
    if (site.is_family_shelter) return 'family';
    if (site.name?.toLowerCase().includes('women')) return 'women';
    return 'men';
  };
  const handleCreateFacility = () => {
    if (!facilityName) {
      toast.error('Please enter a facility name');
      return;
    }
    if (isDemo) {
      if (editingFacility) {
        setDemoSites(prev => prev.map(site => site.id === editingFacility.id ? {
          ...site,
          name: facilityName,
          address: facilityAddress || site.address,
          phone: facilityPhone || site.phone,
          capacity: facilityCapacity ? parseInt(facilityCapacity) : site.capacity
        } : site));
        toast.success('Facility updated (demo mode)');
      } else {
        const newSite = {
          id: `site-${Date.now()}`,
          name: facilityName,
          address: facilityAddress || '',
          phone: facilityPhone || '',
          capacity: facilityCapacity ? parseInt(facilityCapacity) : 0,
          is_active: true,
          is_family_shelter: facilityType === 'family',
          current_occupancy: 0,
          room_total: 0
        };
        setDemoSites(prev => [...prev, newSite]);
        toast.success('Facility created (demo mode)');
      }
    } else {
      const roomTypeConfig = {
        singleRooms: parseInt(roomConfig.singleRooms) || 0,
        sharedRooms: parseInt(roomConfig.sharedRooms) || 0,
        communalRooms: parseInt(roomConfig.communalRooms) || 0,
        communalBeds: parseInt(roomConfig.communalBeds) || 4,
        startingNumber: parseInt(roomConfig.startingNumber) || 101,
      };

      if (editingFacility) {
        updateFacility({
          id: editingFacility.id,
          updates: {
            name: facilityName,
            address: facilityAddress || undefined,
            phone: facilityPhone || undefined,
            capacity: facilityCapacity ? parseInt(facilityCapacity) : undefined,
          },
          roomConfig: roomTypeConfig,
        });
      } else {
        createFacility({
          name: facilityName,
          address: facilityAddress || undefined,
          phone: facilityPhone || undefined,
          capacity: facilityCapacity ? parseInt(facilityCapacity) : undefined,
          is_family_shelter: facilityType === 'family',
          roomConfig: roomTypeConfig,
        });
      }
    }
    setIsAddFacilityOpen(false);
    setEditingFacility(null);
    setFacilityName('');
    setFacilityAddress('');
    setFacilityPhone('');
    setFacilityCapacity('');
    setFacilityType('men');
    setRoomConfig({ singleRooms: '0', sharedRooms: '15', communalRooms: '0', communalBeds: '4', startingNumber: '101' });
  };
  const handleEditFacility = (site: any) => {
    setEditingFacility(site);
    setFacilityName(site.name);
    setFacilityAddress(site.address || '');
    setFacilityPhone(site.phone || '');
    setFacilityCapacity(site.capacity?.toString() || '');
    setFacilityType(getFacilityTypeFromSite(site));
    setRoomConfig({
      singleRooms: '0',
      sharedRooms: '0',
      communalRooms: '0',
      communalBeds: '4',
      startingNumber: '101',
    });
    setIsAddFacilityOpen(true);
  };
  return <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 className="h-5 w-5 text-primary" />
          <span className="text-xl font-semibold">
        </span>
        </div>
                <Dialog open={isAddFacilityOpen} onOpenChange={open => {
        setIsAddFacilityOpen(open);
        if (!open) {
          setEditingFacility(null);
          setFacilityName('');
          setFacilityAddress('');
          setFacilityPhone('');
          setFacilityCapacity('');
          setFacilityType('men');
        }
      }}>
                  {canAddFacility && <DialogTrigger asChild>
                      <Button>
                        <Plus className="h-4 w-4 mr-2" />
                        Add New Facility
                      </Button>
                    </DialogTrigger>}
                  <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingFacility ? 'Edit Facility' : 'Add New Facility'}</DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                      <div className="grid gap-3">
                        <div>
                          <Label>Facility Type*</Label>
                          <Select value={facilityType} onValueChange={v => {
                            setFacilityType(v as FacilityType);
                          }}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select facility type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="family">
                                <div className="flex items-center gap-2">
                                  <Home className="h-4 w-4" />
                                  Family Shelter
                                </div>
                              </SelectItem>
                              <SelectItem value="men">
                                <div className="flex items-center gap-2">
                                  <Users className="h-4 w-4" />
                                  Men's Shelter
                                </div>
                              </SelectItem>
                              <SelectItem value="women">
                                <div className="flex items-center gap-2">
                                  <Users className="h-4 w-4" />
                                  Women's Shelter
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Facility Name*</Label>
                          <Input value={facilityName} onChange={e => setFacilityName(e.target.value)} placeholder="Enter facility name" />
                        </div>
                        <div>
                          <Label>Address</Label>
                          <Input value={facilityAddress} onChange={e => setFacilityAddress(e.target.value)} placeholder="Enter address" />
                        </div>
                        <div>
                          <Label>Phone</Label>
                          <Input value={facilityPhone} onChange={e => setFacilityPhone(e.target.value)} placeholder="Enter phone number" />
                        </div>
                        <div>
                          <Label>Capacity</Label>
                          <Input type="number" value={facilityCapacity} onChange={e => setFacilityCapacity(e.target.value)} placeholder="Total bed capacity" />
                        </div>
                      </div>

                      <RoomConfigSection config={roomConfig} onChange={setRoomConfig} />

                      <Button type="button" onClick={handleCreateFacility} className="w-full">
                        {editingFacility ? 'Save Changes' : 'Create Facility'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Facilities List */}
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Facility Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Clients</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Capacity</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow className={cn("cursor-pointer transition-colors", selectedSite === 'all' && "bg-muted/50")} onClick={() => setSelectedSite('all')}>
                      <TableCell className="font-medium">All Facilities</TableCell>
                      <TableCell className="text-muted-foreground">-</TableCell>
                      <TableCell className="text-muted-foreground">-</TableCell>
                      <TableCell className="text-muted-foreground">-</TableCell>
                      <TableCell className="text-muted-foreground">-</TableCell>
                      <TableCell className="text-muted-foreground">-</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                    {sites.map(site => {
            const clientCount = getClientCount(site.id);
            return <TableRow key={site.id} className={cn("cursor-pointer transition-colors group", selectedSite === site.id && "bg-muted/50")} onClick={() => setSelectedSite(site.id)}>
                          <TableCell>
                            <button className="font-medium text-primary hover:underline flex items-center gap-1" onClick={e => {
                  e.stopPropagation();
                  onSelectFacility?.(site);
                }}>
                              {site.name}
                              <ChevronRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </button>
                          </TableCell>
                          <TableCell>
                            <Badge variant={site.is_family_shelter ? 'default' : 'secondary'}>
                              {getFacilityTypeLabel(getFacilityTypeFromSite(site))}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-normal">
                              <Users className="h-3 w-3 mr-1" />
                              {clientCount}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {site.address ? <div className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {site.address}
                              </div> : '-'}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {site.phone ? <div className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {site.phone}
                              </div> : '-'}
                          </TableCell>
                          <TableCell className="text-sm">{site.capacity || '-'}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={e => {
                  e.stopPropagation();
                  handleEditFacility(site);
                }}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          </TableCell>
                        </TableRow>;
          })}
                  </TableBody>
                </Table>
      </div>
    </div>;
}